/**
 * MailPreferencesCard
 *
 * Lets each user pick, per-flow, whether outbound email is sent by the
 * Financials Ready server (`system`) or composed as a `mailto:` link that
 * opens in their native Gmail / Outlook / Mail.app client.
 *
 * Password-reset is always system-only (user may be unauthenticated when
 * requesting the reset — we can't check their preference).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Server, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const ROWS = [
  { k: "invoices",       label: "Send invoice",           hint: "When you click 'Send' on a draft invoice" },
  { k: "reminders",      label: "Payment reminders",      hint: "The chase email for 60+ day overdue invoices" },
  { k: "reports",        label: "Share reports",          hint: "Emailing a P&L / Balance Sheet / A/R aging PDF" },
  { k: "client_invites", label: "Client portal invites",  hint: "When you invite a client to upload statements" },
];

const AR_DIGEST_HELP = "The Monday-morning A/R digest is a scheduled server email — toggle 'Off' to opt out of receiving it on your account.";

export default function MailPreferencesCard() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mail-preferences");
        setPrefs(r.data);
      } catch { toast.error("Failed to load mail preferences"); }
    })();
  }, []);

  const setValue = async (field, value) => {
    const next = { ...prefs, [field]: value };
    setPrefs(next);
    setSaving(true);
    try {
      const r = await api.patch("/mail-preferences", { [field]: value });
      setPrefs(r.data);
      setSavedAt(Date.now());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!prefs) {
    return (
      <div className="border p-6 bg-white" style={{ borderColor: "var(--ll-border)" }}>
        <Loader2 className="animate-spin" size={16} />
      </div>
    );
  }

  return (
    <div className="border bg-white" style={{ borderColor: "var(--ll-border)" }} data-testid="mail-preferences-card">
      <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white" style={{ borderColor: "var(--ll-border)" }}>
        <div className="text-[10px] uppercase tracking-widest opacity-75">Mail routing</div>
        <div className="font-display font-bold text-sm flex items-center gap-2">
          Where should outbound email come from?
          {saving && <Loader2 size={12} className="animate-spin opacity-80" />}
          {!saving && savedAt > 0 && (Date.now() - savedAt < 2500) && (
            <span className="text-[10px] uppercase tracking-widest opacity-80 flex items-center gap-1"><CheckCircle2 size={11} /> Saved</span>
          )}
        </div>
      </div>

      <div className="p-5 space-y-3">
        <p className="text-xs text-[var(--ll-ink-soft)]">
          "System" sends via Financials Ready instantly. "My mail client" opens Gmail / Outlook / Mail.app / Yahoo in your browser with the message pre-filled — you attach any PDF the app downloads and hit send yourself. Great for keeping replies in your personal inbox.
        </p>

        {ROWS.map((row) => (
          <div
            key={row.k}
            className="flex items-start justify-between gap-3 py-2.5 border-t"
            style={{ borderColor: "var(--ll-border)" }}
            data-testid={`mail-row-${row.k}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium">{row.label}</div>
              <div className="text-xs text-[var(--ll-ink-soft)]">{row.hint}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0" role="group">
              <OptionPill
                active={prefs[row.k] === "system"}
                onClick={() => setValue(row.k, "system")}
                testid={`mail-${row.k}-system`}
                icon={<Server size={11} />}
                label="System"
              />
              <OptionPill
                active={prefs[row.k] === "mailto"}
                onClick={() => setValue(row.k, "mailto")}
                testid={`mail-${row.k}-mailto`}
                icon={<Mail size={11} />}
                label="My mail client"
              />
            </div>
          </div>
        ))}

        <div
          className="flex items-start justify-between gap-3 py-2.5 border-t"
          style={{ borderColor: "var(--ll-border)" }}
          data-testid="mail-row-receipts"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium">Payment receipts</div>
            <div className="text-xs text-[var(--ll-ink-soft)]">
              Sent to the bill-to email after every recorded payment with the running balance.
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <OptionPill
              active={prefs.receipts === "system"}
              onClick={() => setValue("receipts", "system")}
              testid="mail-receipts-system"
              icon={<Server size={11} />}
              label="System"
            />
            <OptionPill
              active={prefs.receipts === "mailto"}
              onClick={() => setValue("receipts", "mailto")}
              testid="mail-receipts-mailto"
              icon={<Mail size={11} />}
              label="My mail client"
            />
            <OptionPill
              active={prefs.receipts === "off"}
              onClick={() => setValue("receipts", "off")}
              testid="mail-receipts-off"
              label="Off"
            />
          </div>
        </div>

        <div
          className="flex items-start justify-between gap-3 py-2.5 border-t"
          style={{ borderColor: "var(--ll-border)" }}
          data-testid="mail-row-ar_digest"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium">Weekly A/R digest to me</div>
            <div className="text-xs text-[var(--ll-ink-soft)]">{AR_DIGEST_HELP}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <OptionPill
              active={prefs.ar_digest === "system"}
              onClick={() => setValue("ar_digest", "system")}
              testid="mail-ar_digest-system"
              icon={<Server size={11} />}
              label="On"
            />
            <OptionPill
              active={prefs.ar_digest === "off"}
              onClick={() => setValue("ar_digest", "off")}
              testid="mail-ar_digest-off"
              label="Off"
            />
          </div>
        </div>
      </div>
    </div>
  );
}


function OptionPill({ active, onClick, testid, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 border transition-colors ${
        active ? "bg-[#0b5d9e] text-white border-[#0b5d9e]" : "bg-white hover:bg-[var(--ll-mint)]"
      }`}
      style={active ? undefined : { borderColor: "var(--ll-border)" }}
    >
      {icon} {label}
    </button>
  );
}
